import React, { useState } from "react";
import {
  Typography,
  Box,
  Button,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Divider,
  Chip,
} from "@mui/material";
import AssessmentIcon from "@mui/icons-material/Assessment";
import StraightenIcon from "@mui/icons-material/Straighten";
import WaterDropIcon from "@mui/icons-material/WaterDrop";
import PeopleIcon from "@mui/icons-material/People";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { UserRole } from "../generated/api-client";
import toast from "react-hot-toast";
import { ToolModal, ToolDefinition } from "../components/ToolModal";

// `team` è lo slug del team che vede la sezione (gli admin vedono tutte le sezioni).
const toolGroups: { groupName: string; team: string; tools: ToolDefinition[] }[] = [
  {
    groupName: "Analisi & Sostenibilità",
    team: "sostenibilita",
    tools: [
      {
        id: "controllo-report-pdf",
        name: "Controllo Report PDF",
        description: "Analizza e verifica i report di sostenibilità in formato PDF",
        icon: <AssessmentIcon />,
        color: "#E53935",
      },
      {
        id: "stime-distanze",
        name: "Stime Distanze",
        description: "Calcola e stima distanze per analisi di mobilità e territorio",
        icon: <StraightenIcon />,
        color: "#00897B",
      },
    ],
  },
  {
    groupName: "Mercato & Controllo Fatture",
    team: "mercato",
    tools: [
      {
        id: "estrazione-bollette-acqua",
        name: "Estrazione Bollette Acqua",
        description: "Estrae i dati dalle bollette dell'acqua in PDF e li raccoglie in un unico Excel",
        icon: <WaterDropIcon />,
        color: "#1E88E5",
      },
    ],
  },
];

const HomePage: React.FC = () => {
  const { user, inviteUser } = useAuth();

  const [activeTool, setActiveTool] = useState<ToolDefinition | null>(null);
  const [toolModalOpen, setToolModalOpen] = useState(false);

  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<UserRole>(UserRole.BaseUser);
  const [inviteLoading, setInviteLoading] = useState(false);

  const isAdmin = user?.role === "admin";
  const visibleGroups = toolGroups.filter(
    (group) => isAdmin || (user?.teams ?? []).includes(group.team)
  );

  const handleOpenTool = (tool: ToolDefinition) => {
    setActiveTool(tool);
    setToolModalOpen(true);
  };

  const handleCloseToolModal = () => {
    setToolModalOpen(false);
    setTimeout(() => setActiveTool(null), 300);
  };

  const handleOpenInviteDialog = () => setInviteDialogOpen(true);

  const handleCloseInviteDialog = () => {
    setInviteDialogOpen(false);
    setInviteEmail("");
    setInviteRole(UserRole.BaseUser);
  };

  const handleInviteUser = async () => {
    if (!inviteEmail.trim()) {
      toast.error("Inserisci un'email");
      return;
    }
    setInviteLoading(true);
    try {
      await inviteUser(inviteEmail.trim(), inviteRole);
      toast.success(`Invito inviato a ${inviteEmail}`);
      handleCloseInviteDialog();
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || "Errore nell'invio dell'invito");
    } finally {
      setInviteLoading(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 3, md: 5 }, maxWidth: 1200, mx: "auto", width: "100%" }}>
      {/* Page heading */}
      <Box sx={{ mb: 5 }}>
        <Typography
          variant="h4"
          sx={{ fontWeight: 700, color: "text.primary", mb: 0.5 }}
        >
          Strumenti
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Seleziona uno strumento per iniziare
        </Typography>
      </Box>

      {/* Tool groups */}
      {visibleGroups.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 5 }}>
          Nessuno strumento disponibile: non sei ancora associato a un team.
          Contatta un amministratore.
        </Typography>
      )}
      {visibleGroups.map((group) => (
        <Box key={group.groupName} sx={{ mb: 5 }}>
          {/* Group header */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, color: "text.primary", whiteSpace: "nowrap" }}
            >
              {group.groupName}
            </Typography>
            <Divider sx={{ flexGrow: 1 }} />
          </Box>

          {/* Tool cards */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2.5,
            }}
          >
            {group.tools.map((tool) => (
              <Box
                key={tool.id}
                onClick={() => handleOpenTool(tool)}
                sx={{
                  width: { xs: "100%", sm: 200 },
                  p: 2.5,
                  borderRadius: "16px",
                  border: "1.5px solid",
                  borderColor: "rgba(0,0,0,0.08)",
                  cursor: "pointer",
                  bgcolor: "background.paper",
                  transition: "all 0.2s ease",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                  userSelect: "none",
                  "&:hover": {
                    borderColor: tool.color + "80",
                    boxShadow: `0 6px 24px ${tool.color}18`,
                    transform: "translateY(-3px)",
                  },
                  "&:active": {
                    transform: "translateY(-1px)",
                  },
                }}
              >
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: "14px",
                    bgcolor: tool.color + "16",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: tool.color,
                    "& svg": { fontSize: "1.6rem" },
                  }}
                >
                  {tool.icon}
                </Box>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, color: "text.primary", lineHeight: 1.3, mb: 0.5 }}
                  >
                    {tool.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      lineHeight: 1.4,
                      display: "block",
                    }}
                  >
                    {tool.description}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      ))}

      {/* Admin section */}
      {isAdmin && (
        <Box sx={{ mt: 6 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, color: "text.primary", whiteSpace: "nowrap" }}
            >
              Amministrazione
            </Typography>
            <Chip label="Admin" size="small" sx={{ bgcolor: "#00A19C18", color: "#00897B", fontWeight: 600 }} />
            <Divider sx={{ flexGrow: 1 }} />
          </Box>

          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              startIcon={<PeopleIcon />}
              component={Link}
              to="/users"
              sx={{ borderRadius: "10px", px: 2.5, py: 1.2 }}
            >
              Gestisci Utenti
            </Button>
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={handleOpenInviteDialog}
              sx={{ borderRadius: "10px", px: 2.5, py: 1.2 }}
            >
              Invita Utente
            </Button>
          </Box>
        </Box>
      )}

      {/* Tool modal */}
      <ToolModal
        open={toolModalOpen}
        onClose={handleCloseToolModal}
        tool={activeTool}
      />

      {/* Invite dialog */}
      <Dialog
        open={inviteDialogOpen}
        onClose={handleCloseInviteDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "1rem" } }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Invita Nuovo Utente</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 1 }}>
            <TextField
              label="Email"
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="nuovo.utente@example.com"
              fullWidth
              required
              slotProps={{ input: { sx: { borderRadius: "8px" } } }}
            />
            <TextField
              select
              label="Ruolo"
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value as UserRole)}
              fullWidth
              required
              slotProps={{ input: { sx: { borderRadius: "8px" } } }}
            >
              <MenuItem value={UserRole.BaseUser}>Utente Base</MenuItem>
              <MenuItem value={UserRole.Mantainer}>Mantainer</MenuItem>
              <MenuItem value={UserRole.Admin}>Amministratore</MenuItem>
            </TextField>
            <Typography variant="body2" color="text.secondary">
              L'utente riceverà un'email con un link per completare la registrazione.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseInviteDialog} disabled={inviteLoading}>
            Annulla
          </Button>
          <Button
            onClick={handleInviteUser}
            variant="contained"
            disabled={inviteLoading || !inviteEmail.trim()}
          >
            {inviteLoading ? <CircularProgress size={24} /> : "Invia Invito"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HomePage;
